import React, { useContext } from "react"
import { useStateMachine } from "../../../context/stateMachine/StateMachineV2"
import { FormattedMessage, useIntl } from "react-intl"
import { BackIcon, Box, Button, Stack, Typography, useTheme } from "@carrotfertility/carotene-core"
import { CarrotCardCostSharePreAuthContext } from "#/components/carrot-card-cost-share/state/CarrotCardCostSharePreAuthContext"
import { CostShareAmountBox } from "#/components/carrot-card-cost-share/steps/shared/CostShareAmountBox"
import { useCreatePreAuth } from "#/components/carrot-card-cost-share/state/useCreatePreAuth"
import { getAmountsAndDates } from "#/components/carrot-card-cost-share/steps/shared/getAmountsAndDates"
import { useExistingPreAuth } from "#/components/carrot-card-cost-share/state/useExistingPreAuth"

export function SubmitAuthorizationStep(): JSX.Element {
  const theme = useTheme()
  const { back, send } = useStateMachine()
  const { preAuthAnswersResponse, dispatchPreAuthResponse } = useContext(CarrotCardCostSharePreAuthContext)
  const { mutate: createPreAuth, isLoading } = useCreatePreAuth()
  const existingPreAuthQuery = useExistingPreAuth()
  const intl = useIntl()

  const { carrotCardAmount, personalPaymentAmount, expirationDate } = getAmountsAndDates(preAuthAnswersResponse, intl)

  function onSubmit(): void {
    createPreAuth(
      { preAuthAnswersId: preAuthAnswersResponse.preAuthAnswersId },
      {
        onSuccess: (response) => {
          dispatchPreAuthResponse({
            type: "SET_RESPONSE",
            response: response
          })
          existingPreAuthQuery.refetch()
          send("")
        }
      }
    )
  }

  return (
    <Box>
      <Typography id="submit-authorization-title" variant="h3">
        <FormattedMessage defaultMessage="Submit Authorization?" />
      </Typography>

      <Stack spacing={theme.tokens.spacing.lg}>
        <Typography>
          <FormattedMessage
            defaultMessage="If you submit, you’ll need to pay the provider or merchant the amounts below by {expirationDate}. "
            values={{ expirationDate }}
          />
        </Typography>

        {personalPaymentAmount ? (
          <Typography>
            <FormattedMessage defaultMessage="The Carrot Card amount is covered by your benefit. The personal amount is what you’ll need to cover. " />
          </Typography>
        ) : null}

        <CostShareAmountBox carrotCardAmount={carrotCardAmount} personalPaymentAmount={personalPaymentAmount} />

        <Box
          sx={(theme) => ({
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBlockStart: theme.tokens.spacing.md
          })}
        >
          <Button variant="text" color="secondary" onClick={back} startIcon={<BackIcon />} disabled={isLoading}>
            <FormattedMessage defaultMessage="Back" />
          </Button>
          <Box sx={(theme) => ({ display: "flex", justifyContent: "flex-end", gap: theme.tokens.spacing.xs })}>
            <Button variant="outlined" color="secondary" onClick={() => send("CLOSE")} disabled={isLoading}>
              <FormattedMessage defaultMessage="Close without submitting" />
            </Button>
            <Button variant="contained" color="primary" type="submit" onClick={onSubmit} disabled={isLoading}>
              <FormattedMessage defaultMessage="Submit" />
            </Button>
          </Box>
        </Box>
      </Stack>
    </Box>
  )
}
